import { format, formatDistanceToNow, differenceInHours, differenceInMinutes, isPast } from "date-fns";

/**
 * Centralized date utility functions for consistent date handling across the app
 */

/**
 * Get time remaining until deadline with localized output
 */
export function getTimeRemaining(deadline: string, language: "en" | "pt" = "en"): string {
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diff = deadlineDate.getTime() - now.getTime();

  if (diff <= 0) {
    return language === "pt" ? "Encerrado" : "Ended";
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    const remaining = language === "pt" ? "restantes" : "remaining";
    return `${hours}h ${remaining}`;
  }

  const remaining = language === "pt" ? "restantes" : "remaining";
  return `${minutes}m ${remaining}`;
}

/**
 * Get time remaining for resolution voting (hours/minutes format)
 */
export function getResolutionTimeRemaining(deadline: string, language: "en" | "pt" = "en"): string {
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diff = deadlineDate.getTime() - now.getTime();

  if (diff <= 0) {
    return language === "pt" ? "Expirado" : "Expired";
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}

/**
 * Check if a deadline has passed
 */
export function isDeadlinePassed(deadline: string): boolean {
  return new Date(deadline).getTime() - new Date().getTime() <= 0;
}

/**
 * Get hours remaining until deadline (numeric)
 */
export function getHoursRemaining(deadline: string): number | null {
  const diff = new Date(deadline).getTime() - Date.now();
  if (diff <= 0) return null;
  return Math.floor(diff / (1000 * 60 * 60));
}

/**
 * Format date for display (localized)
 */
export function formatDate(date: string, language: "en" | "pt" = "en"): string {
  const d = new Date(date);
  return d.toLocaleDateString(language === "pt" ? "pt-BR" : "en-US");
}

/**
 * Format date with time for display (localized)
 */
export function formatDateTime(date: string, language: "en" | "pt" = "en"): string {
  const d = new Date(date);
  return d.toLocaleString(language === "pt" ? "pt-BR" : "en-US");
}

/**
 * Format date for input fields (YYYY-MM-DDTHH:mm format)
 */
export function formatDateForInput(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "yyyy-MM-dd'T'HH:mm");
}

/**
 * Get relative time description (e.g., "2 hours ago", "in 3 days")
 */
export function getRelativeTime(date: string, language: "en" | "pt" = "en"): string {
  const d = new Date(date);
  return formatDistanceToNow(d, { addSuffix: true });
}

/**
 * Get event status display info based on status and deadline
 */
export function getEventStatusDisplay(
  status: string, 
  deadline: string, 
  language: "en" | "pt" = "en"
): { text: string; style: string } {
  const deadlinePassed = isDeadlinePassed(deadline);
  
  if (status === "RESOLVED") {
    return { 
      text: language === "pt" ? "Finalizado" : "Resolved", 
      style: "bg-primary/20 text-primary" 
    };
  }
  
  if (status === "CLOSED" || deadlinePassed) {
    return { 
      text: language === "pt" ? "Encerrado" : "Ended", 
      style: "bg-muted text-muted-foreground" 
    };
  }
  
  return { 
    text: language === "pt" ? "Ao Vivo" : "Live", 
    style: "bg-success/20 text-success" 
  };
}
